import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { Popup, List, Collapse } from 'antd-mobile'
import { useMount, useRequest } from 'ahooks'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import classnames from 'classnames'
import { Arrival, Order } from '@yy/one-icon'
import { useSelector, useDispatch } from '@/store'
import close from '@/assets/icons/close.svg'
import Language from '@/components/Language'
import { usePopstate } from '@/hooks'
import { setLogisticsSearchRecord } from '@/store/logisticsSlice'
import { setOrderSearchRecord } from '@/store/orderSlice'
import { ReactComponent as Dashboard } from '@/assets/icons/dashboard.svg'
import { ReactComponent as DropOff } from '@/assets/icons/dropOff.svg'
import { ReactComponent as HelpCenter } from '@/assets/icons/helpCenter.svg'
// import address from '@/assets/icons/address.svg'
import { ReactComponent as Account } from '@/assets/icons/account.svg'
// import storeManagement from '@/assets/icons/storeManagement.svg'
import { ReactComponent as CountryIcon } from '@/assets/icons/countryIcon.svg'
import { ReactComponent as LanguageIcon } from '@/assets/icons/language.svg'
import { ReactComponent as LogoutIcon } from '@/assets/icons/logout.svg'
import { ReactComponent as Setting } from '@/assets/icons/setting.svg'
import { ReactComponent as CardIcon } from '@/assets/icons/card.svg'
import rightArrow from '@/assets/icons/rightArrow.svg'
import styles from './index.module.less'
import { translateCurrency } from '@/utils'
import { useUserLogin } from '../Authorized/hooks'
import { useWalletList } from '@/hooks/wallet'
import { matchRoutes, MatchedRoute } from 'react-router-config'
import { usePermissionControl } from '@/bizComponents/PermissionControl'
import { jumpToOneclub } from '@/utils/user'
import { getNotifyList, UserMessageNotifyVo } from '@/services/api/notify'
import { useCookieSdk } from '@/hooks/useCookieSdk'

export enum PositionEnum {
  LEFT = 'left',
  BOTTOM = 'bottom',
  TOP = 'top',
  RIGHT = 'right',
}

export interface MenuPopupProps {
  visible: boolean
  position?: PositionEnum
  onMaskClick?: () => void
  routes?: any
}

export interface menu {
  name: string
  childs: menuChild[]
}

export interface menuChild {
  icon: string
  name: string
  /** 右侧是否显示箭头图标，也支持传入 ReactNode 来自定义图标 */
  arrow?: boolean | ReactNode
  /** 类型 */
  type?: 'router' | 'popup' | 'text'
  onClick?: () => void
  path?: string
  id?: string
}

interface Menu {
  /**
   * 路由别名
   * 后续需要动态配置, 请根据该名字与后端数据对应
   */
  alias?: string
  /** 菜单项的标签 */
  tag?: string
  title: string
  path?: string
  key?: string
  icon?: React.FC<any>
  onClick?: (...args: any[]) => any
  className?: Record<string, string>
  subMenus?: Menu[]
  hidden?: boolean
}

const COLLAPSE_KEYS = {
  DASHBOARD: '/dashboard',
  SHIP: '/ship',
  SELLING: '/selling',
  SETTING: '/setting',
  TRACK: '/track',
  SUBSCRIPTION: '/account/subscribe',
  HELPS: '/helps',
  BILL_MANAGEMENT: '/billManagement',
}

const MenuPopup: React.FC<MenuPopupProps> = (props) => {
  const { position = PositionEnum.LEFT, visible, onMaskClick, children, routes } = props
  const userInfo = useSelector((state) => state.auth?.userInfo)
  const languageList = useSelector((state) => state.auth?.languageList)

  const countryMapDics = useSelector((state) => state.dictionary.countryMapDics)
  const dispatch = useDispatch()
  const { i18n, t } = useTranslation()
  const history = useHistory()
  const [defaultCollapseKey, setDefaultCollapseKey] = useState(null)
  const { logoutToOnclub } = useUserLogin()

  const currentPath = history.location?.pathname || ''

  const onLoginOut = () => {
    dispatch(setOrderSearchRecord(null))
    dispatch(setLogisticsSearchRecord(null))
    window.Intercom = undefined
    logoutToOnclub()
    window.sessionStorage.clear()
  }

  /** 切换语言的弹窗 */
  const [visibleLan, setVisibleLan] = useState(false)

  usePopstate(() => {
    setVisibleLan(false)
  })

  useMount(() => {
    for (const key in COLLAPSE_KEYS) {
      if (currentPath.startsWith(COLLAPSE_KEYS[key])) {
        // 设置默认展开的菜单
        setDefaultCollapseKey(COLLAPSE_KEYS[key])
      }
    }
  })

  const pushHandler = useCallback(
    (path) => {
      onMaskClick()
      // 从主菜单进入列表清空搜索记录
      path === '/ship/logistics/list' && dispatch(setLogisticsSearchRecord(null))
      path === '/ship/order/list' && dispatch(setOrderSearchRecord(null))
      history.push(path)
    },
    [onMaskClick, history, dispatch]
  )

  const hasPermission = usePermissionControl()
  const pathHasPermission = useCallback(
    (path: string) => {
      if (!routes) return true
      const matchedRoutes: MatchedRoute<string>[] = matchRoutes(routes, path)
      // 无法匹配，说明不是普通跳转，直接返回
      if (!matchedRoutes.length) return true
      return matchedRoutes.every(({ route: { permissionCode } }) => {
        return permissionCode ? hasPermission(permissionCode) : true
      })
    },
    [routes, hasPermission]
  )

  const mainMenus = useMemo<Menu[]>(() => {
    return [
      {
        // 概览
        alias: 'dashboard',
        title: t('dashboard.index.title'),
        key: COLLAPSE_KEYS.DASHBOARD,
        icon: Dashboard,
        path: '/dashboard',
        className: {
          [styles.active]: currentPath.startsWith(COLLAPSE_KEYS.DASHBOARD),
        },
        hideCountryCodes: [],
      },
      // 发货管理
      {
        alias: 'shipments',
        title: t('menu.shipments'),
        key: COLLAPSE_KEYS.SHIP,
        icon: DropOff,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.SHIP),
          [styles.shipmentMenu]: true,
        },
        subMenus: [
          // 运费试算
          { alias: 'shipping_fee_estimation', title: t('freight.title'), path: '/ship/freight' },
          // 订单管理
          { alias: 'order_management', title: t('help.brief.order'), path: '/ship/order/list' },
          // 运单管理
          { alias: 'logistics_management', title: t('help.brief.waybill'), path: '/ship/logistics/list' },
        ],
      },
      // 轨迹中心
      {
        alias: 'tracking_center',
        title: t('menu.trackCenter'),
        key: COLLAPSE_KEYS.TRACK,
        icon: Arrival,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.TRACK),
          [styles.shipmentMenu]: true,
        },
        subMenus: [
          // 追踪设置
          { alias: 'tracking_settings', title: t('subscribe.trackingSetting.name'), path: '/track/setting' },
        ],
      },
      // 账单管理
      {
        alias: 'bill_management',
        title: t('codBill.billManagement'),
        key: COLLAPSE_KEYS.BILL_MANAGEMENT,
        icon: Order,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.BILL_MANAGEMENT),
        },
        subMenus: [
          // COD账单管理
          { alias: 'cod_bill_management', title: t('codBill.codBillManagement'), path: '/billManagement/codBill' },
          { alias: 'freight_bill_management', title: t('bill.page.title'), path: '/billManagement/freightBillList' },
        ],
      },
      // 订阅
      {
        alias: 'subscription',
        title: t('subscribe.config.title'),
        key: COLLAPSE_KEYS.SUBSCRIPTION,
        icon: Account,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.SUBSCRIPTION),
        },
        path: '/account/subscribe',
      },
      // 设置
      {
        alias: 'settings',
        title: t('menu.setting'),
        key: COLLAPSE_KEYS.SETTING,
        icon: Setting,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.SETTING),
        },
        subMenus: [
          // 地址簿
          { alias: 'address_book', title: t('frame.addressBooks'), path: '/setting/addressbook' },
          // 店铺管理
          { alias: 'store_management', title: t('frame.stores'), path: '/setting/stores/list' },
          { alias: 'provider_management', title: t('setting.shipsetting.title.new'), path: '/setting/account' },
        ],
      },
      // 帮助中心
      {
        alias: 'help_center',
        title: t('menu.helpCenter'),
        key: COLLAPSE_KEYS.HELPS,
        icon: HelpCenter,
        className: {
          [styles._active]: currentPath.startsWith(COLLAPSE_KEYS.HELPS),
        },
        subMenus: [
          // 新手上路
          { alias: 'user_manual', title: t('help.novice'), path: '/helps/guide' },
          // 物流服务
          // { alias: 'logistics_service', title: t('courier.service'), path: '/helps/couriers' },
          // 生态服务
          { alias: 'oneclub_services', title: t('help.eco'), path: '/helps/eco' },
        ],
      },
    ].filter((c) => {
      if (Array.isArray(c?.hideCountryCodes) && c?.hideCountryCodes?.includes(userInfo?.countryCode)) {
        return false
      }

      if (c.path) return pathHasPermission(c.path)
      else if (c.subMenus) {
        c.subMenus = c.subMenus.filter((m) => pathHasPermission(m.path))
        return !!c.subMenus.length
      }
    })
  }, [t, currentPath, pathHasPermission, userInfo?.countryCode])

  const [tagSettings, setTagSettings] = useState<UserMessageNotifyVo>()
  const { run: runQueryList } = useRequest(
    () => {
      const params = {
        notifyType: 'promote',
        pageNum: 1,
        pageSize: 999,
      }

      return getNotifyList(params)
    },
    {
      manual: true,
      onSuccess(res) {
        const { data } = res.data
        const tagSettings = data.reduce((result, item) => {
          const { contentType, relationModules } = item
          return relationModules.reduce((result, name) => {
            result[name] = contentType
            return result
          }, result)
        }, {})

        setTagSettings(tagSettings)
      },
    }
  )

  const finalRouters = useMemo(() => {
    if (typeof tagSettings === 'object' && tagSettings !== null) {
      const travel = (menus) => {
        return menus.reduce((routes, route) => {
          if (Array.isArray(route.subMenus) && route.subMenus.length > 0) {
            const next = travel(route.subMenus)
            return [...routes, route, ...next]
          }

          return [...routes, route]
        }, [])
      }

      travel(mainMenus).forEach((route) => {
        const { alias } = route
        const tag = tagSettings[alias]
        switch (tag) {
          case 'promo':
            route.tag = 'PROMO'
            break
          case 'new':
            route.tag = 'NEW'
            break
        }
      })
    }

    return mainMenus
  }, [mainMenus, tagSettings])

  useMount(() => {
    runQueryList()
  })

  const { defaultWallet } = useWalletList()
  const { openCookie } = useCookieSdk()

  return (
    <>
      <Popup visible={visible} onMaskClick={onMaskClick} position={position} bodyStyle={{ minWidth: '100vw' }}>
        <div className={styles.menuBox}>
          <div className={styles.closeBar}>
            <img src={close} className={styles.icon} onClick={onMaskClick} />
          </div>

          <List className={styles.popMenu}>
            <Collapse accordion arrow={null} defaultActiveKey={defaultCollapseKey}>
              {finalRouters
                .filter((item) => !item?.hidden)
                .map((menu) => {
                  return (
                    <Collapse.Panel
                      key={menu.key}
                      title={
                        <div className={classnames([styles.menuItem, menu.className])} onClick={menu.path ? () => pushHandler(menu.path) : undefined}>
                          <menu.icon className={styles.menuIcon} color="#748399" />
                          {menu.title}
                          {menu?.tag && <span className={styles.menuTag}>{menu?.tag}</span>}
                        </div>
                      }
                    >
                      {menu.subMenus &&
                        menu.subMenus
                          .filter((item) => !item?.hidden)
                          .map((subMenu) => {
                            return (
                              <List.Item
                                key={subMenu.path}
                                className={classnames([styles.subMenu, { [styles.active]: currentPath.startsWith(subMenu.path) }])}
                                arrow={null}
                                onClick={() => pushHandler(subMenu.path)}
                              >
                                {subMenu.title}
                                {subMenu?.tag && <span className={styles.menuTag}>{subMenu?.tag}</span>}
                              </List.Item>
                            )
                          })}
                    </Collapse.Panel>
                  )
                })}
            </Collapse>

            <p className={styles.otherMenu}>{t('menu.category.other')}</p>
            {/* 账号管理 跳转oneclub */}
            <List.Item
              className={styles.menuItem}
              prefix={<Account className={styles.menuIcon} />}
              arrow={false}
              onClick={() => {
                jumpToOneclub('account')
              }}
            >
              {t('menu.account')}
            </List.Item>

            {/* Cookie sdk */}
            {userInfo.countryCode !== 'GB' ? null : (
              <List.Item className={styles.menuItem} prefix={<Setting className={styles.menuIcon} />} arrow={false} onClick={() => openCookie()}>
                {t('menu.cookieSetting')}
              </List.Item>
            )}

            {userInfo?.displayWallet && defaultWallet ? (
              <List.Item
                className={classnames([styles.menuItem, { [styles.active]: currentPath.startsWith('/wallet') }])}
                prefix={<CardIcon className={styles.menuIcon} />}
                arrow={<img src={rightArrow} className={styles.arrow} />}
                onClick={() => pushHandler('/wallet')}
              >
                {t('wallet.wallet')}
                <span className={styles.balance}>
                  {defaultWallet?.currencyCode} {translateCurrency(defaultWallet?.totalBalance)}
                </span>
              </List.Item>
            ) : null}

            <List.Item
              className={styles.menuItem}
              prefix={<LanguageIcon className={styles.menuIcon} />}
              arrow={<img src={rightArrow} className={styles.arrow} />}
              onClick={() => {
                setVisibleLan(true)
              }}
            >
              {languageList.find((lang) => lang.lang == i18n.language)?.label}
            </List.Item>

            {countryMapDics?.[userInfo?.selfArea]?.countryName && (
              <List.Item className={styles.menuItem} prefix={<CountryIcon className={styles.menuIcon} />}>
                {countryMapDics?.[userInfo?.selfArea]?.countryName}
              </List.Item>
            )}

            <List.Item className={styles.menuItem} arrow={null} prefix={<LogoutIcon className={styles.menuIcon} />} onClick={onLoginOut}>
              {t('frame.logout')}
            </List.Item>
          </List>
        </div>
        <Language
          visible={visibleLan}
          onClose={() => {
            setVisibleLan(false)
          }}
        ></Language>
        {children}
      </Popup>
    </>
  )
}

MenuPopup.displayName = 'MenuPopup'

export default MenuPopup
